/**
 * UserTransferList — the Password Safe access picker.
 *
 * Two columns: AVAILABLE users on the left (searchable), CHOSEN users with
 * access on the right. Click a user on the left to bring them across;
 * click a chosen user to send them back. The owner's own row is pinned —
 * shown on the right, not removable (the owner always sees their entry).
 *
 * Props:
 *   users     — full user list [{ id, display_name, username }]
 *   value     — chosen [{ id, name }]
 *   onChange  — (nextChosen) => void
 *   selfId    — the entry OWNER's id (pinned; on create that is the
 *               current user, on edit it is the entry's creator)
 *   selfName  — that owner's display name
 *   pinnedLabel — badge text on the pinned row (default 'owner')
 *   disabled  — read-only mode (non-owners editing an entry)
 */
import React, { useState } from 'react';
import { Box, Flex, Text, Input, VStack, Badge } from '@chakra-ui/react';
import { FiChevronRight, FiChevronLeft, FiLock } from 'react-icons/fi';

export default function UserTransferList({ users = [], value = [], onChange, selfId, selfName, pinnedLabel = 'owner', disabled }) {
  const [search, setSearch] = useState('');
  const chosenIds = new Set(value.map(u => String(u.id)));
  const q = search.trim().toLowerCase();
  const available = users
    .filter(u => String(u.id) !== String(selfId))
    .filter(u => !chosenIds.has(String(u.id)))
    .filter(u => !q
      || (u.display_name || '').toLowerCase().includes(q)
      || (u.username || '').toLowerCase().includes(q));

  const add = (u) => { if (!disabled) onChange([...value, { id: u.id, name: u.display_name || u.username }]); };
  const remove = (u) => { if (!disabled) onChange(value.filter(x => String(x.id) !== String(u.id))); };

  const colStyle = { border: '1px', borderColor: 'gray.200', borderRadius: 'md', bg: 'white', h: '190px', overflowY: 'auto' };

  return (
    <Flex gap={2} align="stretch" opacity={disabled ? 0.6 : 1}>
      {/* Available */}
      <Box flex={1}>
        <Text fontSize="9px" fontWeight="700" color="gray.500" textTransform="uppercase" mb={1}>Available users</Text>
        <Input size="xs" mb={1} placeholder="Search…" value={search}
          onChange={e => setSearch(e.target.value)} isDisabled={disabled} />
        <VStack {...colStyle} spacing={0} align="stretch">
          {available.length === 0 && (
            <Text fontSize="10px" color="gray.400" p={2}>{q ? 'No matches' : 'Everyone has been chosen'}</Text>
          )}
          {available.map(u => (
            <Flex key={u.id} px={2} py={1.5} fontSize="12px" align="center" justify="space-between"
              cursor={disabled ? 'default' : 'pointer'} _hover={disabled ? {} : { bg: 'teal.50' }}
              borderBottom="1px" borderColor="gray.50"
              onClick={() => add(u)}>
              <Text isTruncated>{u.display_name || u.username}</Text>
              {!disabled && <FiChevronRight size={12} color="#38B2AC" />}
            </Flex>
          ))}
        </VStack>
      </Box>
      {/* Chosen */}
      <Box flex={1}>
        <Text fontSize="9px" fontWeight="700" color="gray.500" textTransform="uppercase" mb={1}>
          Has access ({value.length + 1})
        </Text>
        <Box h="24px" mb={1} /> {/* aligns with the search box */}
        <VStack {...colStyle} spacing={0} align="stretch">
          {/* Owner — pinned, always has access */}
          <Flex px={2} py={1.5} fontSize="12px" align="center" justify="space-between"
            bg="gray.50" borderBottom="1px" borderColor="gray.100">
            <Text isTruncated fontWeight="600">{selfName || 'You'}</Text>
            <Badge colorScheme="gray" fontSize="7px"><FiLock size={8} style={{ display: 'inline', verticalAlign: '-1px' }} /> {pinnedLabel}</Badge>
          </Flex>
          {value.map(u => (
            <Flex key={u.id} px={2} py={1.5} fontSize="12px" align="center" justify="space-between"
              cursor={disabled ? 'default' : 'pointer'} _hover={disabled ? {} : { bg: 'red.50' }}
              borderBottom="1px" borderColor="gray.50"
              onClick={() => remove(u)}>
              <Flex align="center" gap={1} minW={0}>{!disabled && <FiChevronLeft size={12} color="#A0AEC0" />}<Text isTruncated>{u.name}</Text></Flex>
            </Flex>
          ))}
        </VStack>
      </Box>
    </Flex>
  );
}
